import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { changePassword } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

const inputCls =
  "rounded-none border-[#0A1F44]/30 bg-transparent focus-visible:ring-1 focus-visible:ring-[#C0982E] focus-visible:border-[#C0982E] font-mono";

const isValid = (pw) => pw.length >= 8 && /\d/.test(pw) && /[a-zA-Z]/.test(pw);

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async (e) => {
    e?.preventDefault();
    if (!isValid(next)) {
      toast.error("New password must be at least 8 characters and include a number and a letter");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setLoading(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated successfully");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally { setLoading(false); }
  };

  const handleOpenChange = (v) => { if (!v) reset(); onOpenChange(v); };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="change-password-dialog" className="rounded-none border border-[#0A1F44] bg-[#F3F5F8] max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#0A1F44] flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#C0982E]" strokeWidth={1.5} /> Change Password
          </DialogTitle>
          <DialogDescription className="text-[#0A1F44]/60">
            Update your admin password. Must be at least 8 characters and include a number and a letter.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Current Password</Label>
            <Input data-testid="cp-current" type="password" className={inputCls} value={current} onChange={(e) => setCurrent(e.target.value)} autoFocus />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">New Password</Label>
            <Input data-testid="cp-new" type="password" className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Confirm New Password</Label>
            <Input data-testid="cp-confirm" type="password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none border-[#0A1F44]" onClick={() => handleOpenChange(false)}>Cancel</Button>
            <Button type="submit" data-testid="cp-submit" disabled={loading} className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E]">
              {loading ? "Updating…" : "Update Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
